/* eslint-disable max-classes-per-file */
import { Injectable, inject } from "@angular/core";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { GeometryInformation } from "../components/widgets/edit-menu/models/edit-models";
import { IMapGeometry, IMapGeometryWicket } from "../map.settings";
import { SpatialService } from "./spatial.service";

@Injectable({
    providedIn: "root"
})
export class MapEditService {
    private readonly spatialService = inject(SpatialService);

    private readonly clip: IMapGeometry[] = [];

    canPasteFromInternal() {
        return this.clip?.length > 0;
    }

    pasteFromInternal(): IMapGeometry[] {
        return this.pasteFromInternalType(null);
    }

    pasteFromInternalType(type: string) {
        if (type != null) {
            return this.clip.filter(c => this.spatialService.getType(c) === type.toLowerCase());
        }
        return this.clip;
    }

    canCopyFromInternal() {
        return true;
    }

    async copyToInternalAndWindowsClipboard(geometries: GeometryInformation[], includeMetadata: boolean, includeAttributes: boolean) {
        this.clip.splice(0);
        let message = "";
        geometries.forEach(g => {
            this.clip.push(g.geometry);
            const attributes = includeAttributes ? this.getAttributesAsString(g) : "";
            const metadata = includeMetadata ? this.getMetadata(g) : "";
            const centerOfGeometry = this.getCenterXY([g.geometry]);
            message += `----------------------------------------\r\n`;
            message += `${(g.label ?? "Okänd")}\r\n`;
            message += `${metadata}\r\n`;
            message += `${attributes}\r\n`;
            message += `Centerpoint\t${centerOfGeometry.x} ${centerOfGeometry.y}\r\n`;
            message += `${(g.geometry as IMapGeometryWicket)?.wkt}\r\n`;
            message += `\r\n`;
        });
        await ObjectUtils.copyToClipboard(message);
    }

    canPasteFromWindowsClipboard(): boolean {
        return true;
    }

    async pasteFromWindowsClipboard(): Promise<ClipboardCoordinate> {
        const unknownContent = await ObjectUtils.pasteFromClipboard();
        return this.validateClipboardCoordinate(unknownContent);
    }

    canCopyToWindowsClipboard(): boolean {
        return true;
    }

    async copyToWindowsClipboard(geometries: GeometryInformation[]): Promise<void> {
        const centerOfGeometries = this.getCenterXY(geometries?.map(x => x.geometry));
        const message = `${centerOfGeometries?.x} ${centerOfGeometries?.y}`;
        await ObjectUtils.copyToClipboard(message);
    }

    hasWindowsClipboard(): boolean {
        return true;
    }

    getCenterXY(geometries: IMapGeometry[]): ClipboardCoordinate {
        if (geometries?.length > 0) {
            const graphics = geometries?.map(g => this.spatialService.toArcGis(g));
            const extent = this.spatialService.getArcGisExtent(graphics);
            return (ObjectUtils.hasValue(extent) ? { x: Math.floor(extent?.center?.x),
                y: Math.floor(extent?.center?.y) } : { x: 0,
                y: 0 }) as ClipboardCoordinate;
        }
        return null;
    }

    private getAttributesAsString(geo: GeometryInformation): string {
        const arr = geo.getAttributesAsArray().map(row => `${row.key}\t${row.value}`);
        return arr.join("\r\n");
    }

    private getMetadata(geo: GeometryInformation): string {
        return `Type\t${geo.metadata?.type}\r\nArea\t${geo.metadata?.area}\r\nLength\t${geo.metadata?.length}`;
    }

    private validateClipboardCoordinate(unknownContent: any): ClipboardCoordinate {
        const coordinate = new ClipboardCoordinate();
        if (!!unknownContent && typeof (unknownContent) == "string" && unknownContent.length > 0 && unknownContent.indexOf(" ") > -1) {
            const items = unknownContent.split(" ");
            if (items.length === 2) {
                const numbers = items.map(str => parseInt(str, 10));
                coordinate.x = numbers[0];
                coordinate.y = numbers[1];
                coordinate.valid = true;
            }
        }
        return coordinate;
    }
}

export class ClipboardCoordinate {
    valid = false;
    x: number;
    y: number;
}

export interface IGeometryMetadata {
    label?: string;
    type?: string;
    area?: number;
    length?: number;
    longitude?: number;
    latitude?: number;
    layer?: any;
    sublayer?: string;
    sublayername?: string;
}
